import { useState, useEffect } from 'react';
import { User, Camera, MapPin, Shield, Fingerprint, Gift } from 'lucide-react';
import MapPickerModal from '../MapPickerModal';

const ProfileCompletionModal = ({
  userProfile,
  setUserProfile,
  handleProfilePicUpload,
  setShowTwoFactorSetup,
  twoFactorEnabled,
  biometricAvailable,
  biometricEnabled,
  enableBiometric,
  handleProfileSubmit
}) => {
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const patch = {};
    if (!userProfile.city) patch.city = 'Gurgaon';
    if (!userProfile.state) patch.state = 'Haryana';
    if (!userProfile.country) patch.country = 'India';
    if (Object.keys(patch).length > 0) {
      setUserProfile(prev => ({ ...prev, ...patch }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const e = {};
    if (!userProfile.name?.trim()) e.name = 'Full name is required';
    if (!userProfile.email?.trim()) e.email = 'Email is required';
    if (!userProfile.address?.trim()) e.address = 'Delivery address is required';
    if (!userProfile.city?.trim()) e.city = 'City is required';
    if (!userProfile.state?.trim()) e.state = 'State is required';
    if (!userProfile.pincode || String(userProfile.pincode).length !== 6) e.pincode = 'Valid 6-digit pincode required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) handleProfileSubmit();
  };

  const handleMapConfirm = (data) => {
    setUserProfile(prev => ({
      ...prev,
      address: data.address || prev.address,
      city: data.city || prev.city,
      state: data.state || prev.state,
      pincode: data.pincode || prev.pincode,
      country: data.country || prev.country,
      latitude: data.latitude ?? prev.latitude,
      longitude: data.longitude ?? prev.longitude,
    }));
    setShowMapPicker(false);
  };

  return (
    <>
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl my-4 max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center overflow-hidden">
              {userProfile.profilePic ? (
                <img src={userProfile.profilePic} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User size={40} className="text-purple-600" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full cursor-pointer hover:bg-purple-700 transition-all shadow-lg">
              <Camera size={16} />
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicUpload}
                className="hidden"
              />
            </label>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Complete Your Profile</h2>
          <p className="text-gray-600 text-sm">Help us serve you better</p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userProfile.name}
              onChange={(e) => { setUserProfile({ ...userProfile, name: e.target.value }); setErrors(p => ({ ...p, name: '' })); }}
              placeholder="Enter your full name"
              className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={userProfile.email}
              onChange={(e) => { setUserProfile({ ...userProfile, email: e.target.value }); setErrors(p => ({ ...p, email: '' })); }}
              placeholder="your.email@example.com"
              className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all ${errors.email ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Mobile Number
            </label>
            <input
              type="tel"
              value={userProfile.mobile || ''}
              onChange={(e) => setUserProfile({ ...userProfile, mobile: e.target.value })}
              placeholder="Mobile number"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Delivery Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <textarea
                value={userProfile.address}
                onChange={(e) => { setUserProfile({ ...userProfile, address: e.target.value }); setErrors(p => ({ ...p, address: '' })); }}
                placeholder="Enter your delivery address"
                rows="3"
                className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all resize-none ${errors.address ? 'border-red-400' : 'border-gray-200'}`}
              />
              <MapPin size={20} className="absolute right-4 top-3 text-gray-400 pointer-events-none" />
            </div>
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            <button
              type="button"
              onClick={() => setShowMapPicker(true)}
              className="mt-2 flex items-center gap-2 text-sm text-purple-600 font-medium hover:text-purple-700"
            >
              <MapPin size={15} /> Pick from Google Map
            </button>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userProfile.city}
              onChange={(e) => { setUserProfile({ ...userProfile, city: e.target.value }); setErrors(p => ({ ...p, city: '' })); }}
              placeholder="Enter your city"
              className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all ${errors.city ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength="6"
                value={userProfile.pincode}
                onChange={(e) => { setUserProfile({ ...userProfile, pincode: e.target.value.replace(/\D/g, '') }); setErrors(p => ({ ...p, pincode: '' })); }}
                placeholder="6-digit pincode"
                className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all ${errors.pincode ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userProfile.state || ''}
                onChange={(e) => { setUserProfile({ ...userProfile, state: e.target.value }); setErrors(p => ({ ...p, state: '' })); }}
                placeholder="Enter state"
                className={`w-full border-2 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all ${errors.state ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              value={userProfile.country || 'India'}
              onChange={(e) => setUserProfile({ ...userProfile, country: e.target.value })}
              placeholder="India"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all"
            />
          </div>
        </div>

        {/* Security Features */}
        <div className="space-y-3 mb-6">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield size={24} className="text-purple-600" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">Two-Factor Authentication</h4>
                  <p className="text-xs text-gray-600">Add extra security to your account</p>
                </div>
              </div>
              <button
                onClick={() => setShowTwoFactorSetup(true)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-purple-700 transition-all"
              >
                {twoFactorEnabled ? 'Enabled' : 'Enable'}
              </button>
            </div>
          </div>

          {biometricAvailable && (
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Fingerprint size={24} className="text-purple-600" />
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Biometric Login</h4>
                    <p className="text-xs text-gray-600">Use fingerprint or face ID</p>
                  </div>
                </div>
                <button
                  onClick={enableBiometric}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-purple-700 transition-all"
                >
                  {biometricEnabled ? 'Enabled' : 'Enable'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Gift size={20} className="text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 text-sm mb-1">Welcome Offer! 🎉</h4>
              <p className="text-xs text-gray-600">
                Complete your profile and get ₹50 off on your first order above ₹200
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          className="w-full bg-purple-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
        >
          Complete Profile & Start Shopping
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          * Required fields
        </p>
      </div>
    </div>

    {showMapPicker && (
      <MapPickerModal
        onClose={() => setShowMapPicker(false)}
        onConfirm={handleMapConfirm}
        initialLat={userProfile.latitude || null}
        initialLng={userProfile.longitude || null}
      />
    )}
    </>
  );
};

export default ProfileCompletionModal;